"use client";

import * as SheetPrimitive from "@radix-ui/react-dialog";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { ActionButtons } from "../components/actionButtons";
import { cn } from "../lib/utils";
import { DialogProps } from "./dialog";
import { Icon } from "./icon";

interface SheetProps
  extends DialogProps, React.ComponentPropsWithoutRef<typeof SheetContent> {
  hasHeader?: boolean;
  hasFooter?: boolean;
  side?: SheetContentProps["side"];
}

/**
 * Radix UI (API reference): https://www.radix-ui.com/docs/primitives/components/dialog#api-reference
 *
 * shadcn/ui docs: https://ui.shadcn.com/docs/components/sheet
 */
const Sheet = React.forwardRef<
  React.ElementRef<typeof SheetContent>,
  SheetProps
>(
  (
    {
      open,
      onOpenChange,
      defaultOpen,
      children,
      heading,
      description,
      actionButtonProps,
      cancelButtonProps,
      hasCancelButton = true,
      hasActionButton = true,
      hasHeader = true,
      hasFooter = true,
      ...props
    },
    ref,
  ) => (
    <SheetPrimitive.Root
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
      open={open}
    >
      <SheetContent
        ref={ref}
        {...{
          ...props,
          className: cn("overflow-auto", props.className),
        }}
      >
        <SheetHeader
          className={cn({
            "sr-only": !hasHeader,
          })}
        >
          <SheetTitle>{heading}</SheetTitle>
          {description && <SheetDescription>{description}</SheetDescription>}
        </SheetHeader>

        {children}

        {hasFooter && (hasCancelButton || hasActionButton) && (
          <SheetFooter>
            <ActionButtons
              // Provide at least an empty object to ensure these buttons are always displayed.
              // This requires them to be hidden explicitly to enforce a Cancel / Accept
              // button pattern.
              actionButtonProps={actionButtonProps || {}}
              // Provide at least an empty object to ensure these buttons are always displayed.
              // This requires them to be hidden explicitly to enforce a Cancel / Accept
              // button pattern.
              cancelButtonProps={cancelButtonProps || {}}
              hasActionButton={hasActionButton}
              hasCancelButton={hasCancelButton}
              inDialog
            />
          </SheetFooter>
        )}
      </SheetContent>
    </SheetPrimitive.Root>
  ),
);
Sheet.displayName = SheetPrimitive.Root.displayName;

// const SheetTrigger = SheetPrimitive.Trigger;

// const SheetClose = SheetPrimitive.Close;

const SheetPortal = SheetPrimitive.Portal;

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Overlay
    className={cn(
      "fixed inset-0 z-[1000] bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
    ref={ref}
  />
));
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

const sheetVariants = cva(
  "fixed z-[1000] gap-4 bg-background p-6 shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500 data-[state=open]:animate-in data-[state=closed]:animate-out",

  {
    variants: {
      side: {
        top: "inset-x-0 top-0 max-h-screen border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
        bottom:
          "inset-x-0 bottom-0 max-h-screen border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
        left: "inset-y-0 left-0 h-full w-3/4 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm",
        right:
          "inset-y-0 right-0 h-full w-3/4 border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm",
      },
    },
    defaultVariants: {
      side: "right",
    },
  },
);

interface SheetContentProps
  extends
    React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
    VariantProps<typeof sheetVariants> {}

const SheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  SheetContentProps
>(
  (
    {
      side = "right",
      className,
      children,
      "aria-describedby": ariaDescribedBy,
      ...props
    },
    ref,
  ) => (
    <SheetPortal>
      <SheetOverlay />
      <SheetPrimitive.Content
        aria-describedby={ariaDescribedBy}
        className={cn(sheetVariants({ side }), className)}
        ref={ref}
        {...props}
      >
        {children}

        <SheetPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary">
          <Icon icon="close" size="sm" weight="bold" />
          <span className="sr-only">Close</span>
        </SheetPrimitive.Close>
      </SheetPrimitive.Content>
    </SheetPortal>
  ),
);
SheetContent.displayName = SheetPrimitive.Content.displayName;

const SheetHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-2 text-center sm:text-left",
      className,
    )}
    {...props}
  />
);
SheetHeader.displayName = "SheetHeader";

const SheetFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className,
    )}
    {...props}
  />
);
SheetFooter.displayName = "SheetFooter";

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    className={cn("text-lg font-semibold text-foreground", className)}
    ref={ref}
    {...props}
  />
));
SheetTitle.displayName = SheetPrimitive.Title.displayName;

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    className={cn("text-sm text-muted-foreground", className)}
    ref={ref}
    {...props}
  />
));
SheetDescription.displayName = SheetPrimitive.Description.displayName;

const SheetRoot = SheetPrimitive.Root;
const SheetClose = SheetPrimitive.Close;
const SheetTrigger = SheetPrimitive.Trigger;

export {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
  SheetRoot,
  SheetTitle,
  SheetTrigger,
  type SheetContentProps,
  type SheetProps,
};
